import * as logger from 'OK/logger';

/**
 * Селектор логотипа в тулбаре
 */
const TOOLBAR_LOGO_SELECTOR = '.toolbar_logo-w';
/**
 * При переходе от логотипа к дропдауну событие `mouseenter` срабатывает повторно.
 * Чтобы ошибочно не логировать лишние показы,
 * считаем 2 показа с потерей курсора на время до `MAX_CURSOR_LOSS_INTERVAL_MS` мс одним показом
 */
const MAX_CURSOR_LOSS_INTERVAL_MS = 900;

const LOG_OPERATION = 'toolbarLogoGrowl';

export class ToolbarLogoGrowl {
    private growlElement: HTMLDivElement | null = null;
    private growlId: string | null = null;
    private hoverLogDelayMs!: number;
    private hoverManager: HoverManager | null = null;
    private unsubscribe!: IUnsubscribe;
    private timer: Timer | null = null;

    public activate(growlElement: HTMLDivElement): void {
        const toolbarLogoElement = document.querySelector<HTMLDivElement>(TOOLBAR_LOGO_SELECTOR);

        if (!toolbarLogoElement) {
            return;
        }

        this.growlElement = growlElement;
        this.growlId = this.growlElement.getAttribute('data-growl-id');
        this.hoverLogDelayMs = +this.growlElement.getAttribute('data-log-delay-ms')!;

        // по ховеру на лого в тулбаре отображается тултип, логируем это событие
        this.hoverManager = new HoverManager(toolbarLogoElement, MAX_CURSOR_LOSS_INTERVAL_MS);
        this.unsubscribe = this.hoverManager.subscribe(this.hoverChangeHandler);

        this.growlElement.addEventListener('click', this.logClick);
    }

    private hoverChangeHandler = (state: HoverState) => {
        switch (state) {
            case HoverState.START:
                this.timer = Timer.create(this.hoverLogDelayMs, this.logHover)
                this.timer.start();
                return;

            case HoverState.PAUSE:
                this.timer?.pause();
                return;

            case HoverState.CONTINUE:
                this.timer?.start();
                return;

            case HoverState.END:
                if (this.timer) {
                    this.timer.stop();
                    this.timer = null;
                }
                return;
        }
    }

    private logClick = (): void => {
        this.log('click');
    };

    private logHover = (): void => {
        this.log('hover');
    };

    private log = (action: 'hover' | 'click'): void => {
        logger.success(LOG_OPERATION, action, this.growlId);
    };

    public deactivate(): void {
        if (this.unsubscribe) {
            this.unsubscribe();
        }

        if (this.growlElement) {
            this.growlElement.removeEventListener('click', this.logClick);
            this.growlElement = null;
        }

        if (this.hoverManager) {
            this.hoverManager.destroy();
            this.hoverManager = null;
        }

        if (this.timer) {
            this.timer.stop();
            this.timer = null;
        }

        this.growlId = null;
    }
}

enum HoverState {
    START,
    PAUSE,
    CONTINUE,
    END,
}

type IHoverManagerSubscriber = (state: HoverState) => void;

type IUnsubscribe = () => void;

class HoverManager {
    private readonly maxCursorLossInterval: number;
    private element: HTMLElement | null;
    private isHover = false;
    private timer?: number;
    private subscribers: Array<IHoverManagerSubscriber> | null = [];

    public constructor(element: HTMLElement, maxCursorLossInterval: number) {
        this.element = element;
        this.maxCursorLossInterval = maxCursorLossInterval;
        this.element.addEventListener('mouseenter', this.mouseEnterListener);
        this.element.addEventListener('mouseleave', this.mouseLeaveListener);
    }

    public destroy(): void {
        if (this.element) {
            this.element.removeEventListener('mouseenter', this.mouseEnterListener);
            this.element.removeEventListener('mouseleave', this.mouseLeaveListener);
            this.subscribers = null;
            this.element = null;
        }
    }

    public subscribe(subscriber: IHoverManagerSubscriber): IUnsubscribe {
        this.subscribers?.push(subscriber);

        return () => {
            if (!this.subscribers) {
                return;
            }

            const idx = this.subscribers.indexOf(subscriber);

            if (idx >= 0) {
                this.subscribers.splice(idx, 1);
            }
        }
    }

    private notify(eventType: HoverState) {
        if (this.subscribers) {
            this.subscribers.forEach(subscriber => subscriber(eventType));
        }
    }

    private mouseEnterListener = (): void => {
        const isHoverPrev = this.isHover;

        if (isHoverPrev) {
            // если isHoverPrev = true, то ховер уже срабатывал и с момента `mouseleave` еше не прошло `maxCursorLossInterval` мс,
            // поэтому считаем, что это продолжение одного и того же ховера
            this.notify(HoverState.CONTINUE);
        } else {
            // иначе ховер сработал в первый раз
            this.notify(HoverState.START);
        }

        this.setHoverState(true);
    }

    private mouseLeaveListener = (): void => {
        this.notify(HoverState.PAUSE);
        this.setHoverState(false);
    }

    private setHoverState(isHover: boolean): void {
        clearTimeout(this.timer);

        if (isHover) {
            this.isHover = true;
            return;
        }

        this.timer = setTimeout(() => {
            this.isHover = false;
            // уведомляем подписчиков о потере ховера
            this.notify(HoverState.END);
        }, this.maxCursorLossInterval);
    }
}

type ITimerCallback = () => void;

class Timer {
    private readonly callback: ITimerCallback;
    private timer?: number;
    private startTime?: number;
    private remainingTime: number;

    constructor(time: number, callback: ITimerCallback) {
        this.remainingTime = time;
        this.callback = callback;
    }

    public start(): void {
        if (this.remainingTime > 0) {
            this.startTime = Date.now();
            this.timer = setTimeout(this.callback, this.remainingTime);
        }
    }

    public pause(): void {
        clearTimeout(this.timer);
        const pauseTime = Date.now();
        this.remainingTime = this.remainingTime - (pauseTime - this.startTime!);
    }

    public stop(): void {
        clearTimeout(this.timer);
    }

    public static create(time: number, callback: ITimerCallback): Timer {
        return new Timer(time, callback);
    }
}
